import { motion, useInView } from "framer-motion";
import { Code, Scan, ShieldCheck } from "lucide-react";
import { useRef } from "react";
import TypingTerminal from "./how-it-works/TypingTerminal";
import RadarScanner from "./how-it-works/RadarScanner";
import ParticleFlow from "./how-it-works/ParticleFlow";
import EnergyBeam from "./how-it-works/EnergyBeam";

const cardBase =
  "relative rounded-xl border border-border/50 bg-surface/30 backdrop-blur-md p-8 md:p-10 transition-all duration-500 hover:border-primary/40 hover:glow-cyan-strong hover:scale-[1.01] overflow-hidden";

const HowItWorks = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

  return (
    <section id="how-it-works" ref={sectionRef} className="relative py-24 md:py-36 px-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-14 md:mb-20"
        >
          <p className="text-[10px] font-mono text-primary uppercase tracking-[0.3em] mb-4">
            How It Works
          </p>
          <h2 className="font-display font-extrabold text-4xl md:text-6xl text-foreground tracking-tight">
            Three steps.{" "}
            <span className="text-primary text-glow-cyan">Zero</span>{" "}
            complexity.
          </h2>
          <p className="mt-5 text-base font-mono text-muted-foreground max-w-lg">
            From script tag to full protection in under a minute.
          </p>
        </motion.div>

        <div className="flex flex-col gap-6 md:gap-4">
          {/* ── STEP 01 — INSTALL ── Full-width hero card */}
          <motion.div
            initial={{ opacity: 0, x: -60 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ type: "spring", stiffness: 80, damping: 20, delay: 0.2 }}
          >
            <div className={cardBase}>
              {/* Cyan top edge glow */}
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent" />

              {/* Watermark */}
              <span className="absolute -top-6 -right-4 font-data text-[120px] font-bold text-primary/[0.03] select-none pointer-events-none leading-none -rotate-6">
                01
              </span>

              <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
                {/* Text side */}
                <div>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-lg border border-primary/20 bg-primary/5 flex items-center justify-center animate-float">
                      <Code className="w-6 h-6 text-primary" />
                    </div>
                    <span className="font-data text-xs font-bold text-primary/50 tracking-[0.2em]">
                      STEP 01
                    </span>
                  </div>
                  <h3 className="font-display font-black text-3xl md:text-4xl text-foreground mb-3">
                    Install
                  </h3>
                  <p className="text-base text-muted-foreground font-mono leading-relaxed max-w-sm">
                    One script tag. Any stack. Thirty seconds. Paste it and forget it — we handle the rest.
                  </p>
                </div>

                {/* Terminal side */}
                <TypingTerminal inView={isInView} />
              </div>
            </div>
          </motion.div>

          {/* Energy beam connector */}
          <div className="hidden md:flex items-center justify-center px-20">
            <EnergyBeam inView={isInView} />
          </div>
          <div className="md:hidden flex justify-center h-12">
            <EnergyBeam inView={isInView} vertical />
          </div>

          {/* ── STEP 02 — DETECT ── Two-column */}
          <motion.div
            initial={{ opacity: 0, x: 60 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ type: "spring", stiffness: 80, damping: 20, delay: 0.5 }}
          >
            <div className={cardBase}>
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
              <span className="absolute -top-6 -right-4 font-data text-[120px] font-bold text-primary/[0.03] select-none pointer-events-none leading-none -rotate-6">
                02
              </span>

              <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
                {/* Radar side */}
                <div className="order-2 md:order-1">
                  <RadarScanner inView={isInView} />
                </div>

                {/* Text side */}
                <div className="order-1 md:order-2">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-lg border border-primary/20 bg-primary/5 flex items-center justify-center animate-float">
                      <Scan className="w-6 h-6 text-primary" />
                    </div>
                    <span className="font-data text-xs font-bold text-primary/50 tracking-[0.2em]">
                      STEP 02
                    </span>
                  </div>
                  <h3 className="font-display font-black text-3xl md:text-4xl text-foreground mb-3">
                    Detect
                  </h3>
                  <p className="text-base text-muted-foreground font-mono leading-relaxed max-w-sm">
                    Fingerprints every visitor — mouse movements, JS engine, headers, behavioral patterns. 47 signals in under 50ms.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Energy beam connector */}
          <div className="hidden md:flex items-center justify-center px-20">
            <EnergyBeam inView={isInView} />
          </div>
          <div className="md:hidden flex justify-center h-12">
            <EnergyBeam inView={isInView} vertical />
          </div>

          {/* ── STEP 03 — PROTECT ── Two-column reversed */}
          <motion.div
            initial={{ opacity: 0, x: -60 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ type: "spring", stiffness: 80, damping: 20, delay: 0.8 }}
          >
            <div className={cardBase}>
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
              <span className="absolute -top-6 -right-4 font-data text-[120px] font-bold text-primary/[0.03] select-none pointer-events-none leading-none -rotate-6">
                03
              </span>

              <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
                {/* Text side */}
                <div>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-lg border border-primary/20 bg-primary/5 flex items-center justify-center animate-float">
                      <ShieldCheck className="w-6 h-6 text-primary" />
                    </div>
                    <span className="font-data text-xs font-bold text-primary/50 tracking-[0.2em]">
                      STEP 03
                    </span>
                  </div>
                  <h3 className="font-display font-black text-3xl md:text-4xl text-foreground mb-3">
                    Protect
                  </h3>
                  <p className="text-base text-muted-foreground font-mono leading-relaxed max-w-sm">
                    Bots blocked. Humans pass. Your analytics become honest. Watch threats get neutralized in real-time.
                  </p>
                </div>

                {/* Particle flow side */}
                <ParticleFlow inView={isInView} />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
